import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { loadGoogleMapsScript } from '../utils/loadGoogleMapsScript';
import { log } from '../utils/commonUtils';

interface GoogleMapsContextType {
    mapsLoaded: boolean;
    placePredictions: google.maps.places.AutocompletePrediction[];
    getPlacePredictions: (request: google.maps.places.AutocompleteRequest) => void;
    isPlacePredictionsLoading: boolean;
    getPlaceDetails: (placeId: string, fields?: string[]) => Promise<google.maps.places.Place>;
    searchPlacesByText: (query: string, fields?: string[]) => Promise<google.maps.places.Place[]>;
}

const GoogleMapsContext = createContext<GoogleMapsContextType>({
    mapsLoaded: false,
    placePredictions: [],
    getPlacePredictions: () => {},
    isPlacePredictionsLoading: false,
    getPlaceDetails: async () => {
        throw new Error('Maps not loaded yet');
    },
    searchPlacesByText: async () => {
        throw new Error('Maps not loaded yet');
    }
});

export const useGoogleMaps = () => useContext(GoogleMapsContext);

export const GoogleMapsProvider = ({ children }: { children: React.ReactNode }) => {
    const [mapsLoaded, setMapsLoaded] = useState(false);
    const [placePredictions, setPlacePredictions] = useState<google.maps.places.AutocompletePrediction[]>([]);
    const [isPlacePredictionsLoading, setIsPlacePredictionsLoading] = useState(false);

    const debounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        loadGoogleMapsScript()
            .then(() => {
                setMapsLoaded(true);
            })
            .catch((err) => {
                console.error('Failed to load Google Maps:', err);
            });
    }, []);

    const getPlacePredictions = async (request: google.maps.places.AutocompleteRequest, debounceMs = 300) => {
        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
        }

        debounceTimeoutRef.current = setTimeout(async () => {
            if (!request.input) {
                setPlacePredictions([]);
                return;
            }

            setIsPlacePredictionsLoading(true);
            try {
                const results = await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
                    input: request.input,
                    includedPrimaryTypes: request.includedPrimaryTypes || []
                });

                // Transform suggestions to match AutocompletePrediction[]
                const mapped = results.suggestions.map((s): google.maps.places.AutocompletePrediction => {
                const mainText = s.placePrediction?.mainText?.text ?? '';
                const secondaryText = s.placePrediction?.secondaryText?.text ?? '';
                const descriptionVal = secondaryText ? `${mainText} ${secondaryText}` : mainText;
                    return {
                        description: s.placePrediction?.toPlace().displayName ?? descriptionVal.trim(),
                        place_id: s.placePrediction.placeId,
                        structured_formatting: {
                            main_text: mainText,
                            secondary_text: secondaryText,
                            main_text_matched_substrings: []
                        },
                        types: s.placePrediction.types ?? [],
                        matched_substrings: [],
                        terms: []
                    };
                });

                setPlacePredictions(mapped);
            } catch (e) {
                log('Error fetching suggestions:', e);
                setPlacePredictions([]);
            } finally {
                setIsPlacePredictionsLoading(false);
            }
        }, debounceMs);
    };

    const getPlaceDetails = async (
        placeId: string,
        fields: string[] = ['displayName', 'id', 'location', 'formattedAddress', 'addressComponents']
    ): Promise<google.maps.places.Place> => {
        const mapPlace = new google.maps.places.Place({ id: placeId });
        const { place } = await mapPlace.fetchFields({ fields });
        return place;
    };

    const searchPlacesByText = async (
        query: string,
        fields: string[] = ['displayName', 'id', 'location', 'formattedAddress']
    ): Promise<google.maps.places.Place[]> => {
        if (!query) return [];
        const { places } = await google.maps.places.Place.searchByText({ fields, textQuery: query });
        return places || [];
    };

    return (
        <GoogleMapsContext.Provider
            value={{
                mapsLoaded,
                placePredictions,
                getPlacePredictions,
                isPlacePredictionsLoading,
                getPlaceDetails,
                searchPlacesByText
            }}
        >
            {children}
        </GoogleMapsContext.Provider>
    );
};
